import { useRef, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import styled from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';
import { PerformanceOverview, PerformanceOverviewPeriod } from 'common/api-types/recommendations';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { formatNumber } from 'common/utils/numberUtils';

import { getMetricValueUnit } from '../utils';
import { PerformanceMetricTooltip } from './PerformanceMetricTooltip';

const Label = styled.div`
  color: ${color.textSubdued};
  height: 40px;

  ${typographyCss.caption};
`;

const LastValue = styled(Stack)`
  ${typographyCss.title};
`;

const Goal = styled.div`
  ${typographyCss.caption};
  color: ${color.textSubdued};
`;

const Symbol = styled.span`
  color: ${color.textSubdued};
`;

const NoPerformanceData = styled.div`
  color: ${color.textSubdued};
`;

const TargetIndicator = styled(Stack)<{ $color: string }>`
  ${typographyCss.footnote};

  color: ${({ $color }) => $color};
  align-self: flex-end;
  margin-top: ${dimension.spaceNarrowXsmall};
`;

const LeftContent = styled(Stack)`
  flex: 0 0 50%;
`;

const RightContent = styled(Stack)`
  flex-grow: 1;
`;

const Chart = styled(Stack)`
  height: 100px;
  width: 100%;
`;

type Props = {
  metric: PerformanceOverview | undefined;
  label: string;
  timePeriod: PerformanceOverviewPeriod;
};

const deltaColors = {
  GOOD: color.textPositive,
  BAD: color.textNegative,
  NEUTRAL: color.textSubdued,
};

const deltaUnits = {
  SELECTION_COUNT: '%',
  AVERAGE_BASKET_VALUE: '%',
  PURE_PRODUCT_MARGIN: 'bps',
  SHRINKAGE: 'bps',
};

const deltaStateIcons = {
  GOOD: icons.arrowUpCircle,
  BAD: icons.arrowDownCircle,
  NEUTRAL: undefined,
};

export const PerformanceMetric = ({ metric, label, timePeriod }: Props) => {
  const f = useFormatMessage();
  const locale = useUserLocale();
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const chartRef = useRef<HTMLDivElement>(null);
  let impactIcon;

  if (!metric) {
    return null;
  }

  const { target, values } = metric;

  const { metricType } = metric;
  const { leftUnit, rightUnit } = getMetricValueUnit()[metricType];
  const impactValue = parseFloat(metric.delta.value);

  if (impactValue > 0) {
    impactIcon = deltaStateIcons.GOOD;
  } else if (impactValue < 0) {
    impactIcon = deltaStateIcons.BAD;
  } else {
    impactIcon = deltaStateIcons.NEUTRAL;
  }

  const formatIfSelectionCount = (value: string | undefined) =>
    metricType === 'SELECTION_COUNT' ? formatNumber(locale, Number(value)) : value;

  const lastValue = values[values.length - 1]?.value;
  const formattedLastValue = formatIfSelectionCount(lastValue);
  const formattedTarget = formatIfSelectionCount(target);

  const handleMouseMove = (nextState: { activeCoordinate?: { x: number; y: number } }) => {
    // Position tooltip to prevent it from being cropped
    if (nextState.activeCoordinate) {
      const { x, y } = nextState.activeCoordinate;
      const tooltipWidth = 150;
      const tooltipHeight = 60; // Approximate tooltip height
      const chartWidth = chartRef.current?.offsetWidth;
      const chartHeight = chartRef.current?.offsetHeight;

      if (!chartWidth || !chartHeight) {
        return;
      }

      // Prevent horizontal cropping
      const tooltipPositionX = x + tooltipWidth > chartWidth ? x - tooltipWidth : x;

      // Prevent vertical cropping
      const tooltipPositionY = y + tooltipHeight > chartHeight ? y - tooltipHeight : y;

      setTooltipPosition({ x: tooltipPositionX, y: tooltipPositionY });
    }
  };

  return (
    <>
      <LeftContent gap="regularMedium">
        <Label>{label}</Label>
        {metric.values.length > 0 ? (
          <>
            <LastValue
              direction="row"
              alignItems="center"
              gap="narrowSmall"
              data-test-id="latest-value"
            >
              {leftUnit && <Symbol>{leftUnit}</Symbol>}
              {formattedLastValue}
              {rightUnit && <Symbol>{rightUnit}</Symbol>}
            </LastValue>
            {target && (
              <Goal data-test-id="goal-value">
                {f('selection_recommendations.performance_overview_goal', {
                  value: formattedTarget,
                  leftUnit,
                  rightUnit,
                })}
              </Goal>
            )}
          </>
        ) : (
          <NoPerformanceData data-test-id="no-performance-data">
            {f('selection_recommendations.performance_overview_no_data')}
          </NoPerformanceData>
        )}
      </LeftContent>
      <RightContent gap="regularMedium">
        <TargetIndicator
          data-test-id="delta-indicator"
          direction="row"
          alignItems="center"
          gap="narrowSmall"
          $color={deltaColors[metric.delta.impact]}
        >
          {metric.values.length > 0 && (
            <>
              <Stack direction="row" data-test-id="delta-value">
                {impactValue > 0 ? `+${impactValue}` : impactValue}
                {deltaUnits[metricType]}
              </Stack>
              {impactIcon && <Icon source={impactIcon} size="small" />}
            </>
          )}
        </TargetIndicator>
        <Chart ref={chartRef}>
          <ResponsiveContainer height={100}>
            <AreaChart data={values} onMouseMove={handleMouseMove}>
              <defs>
                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="1%" stopColor={color.textBrand} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={color.textBrand} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={color.textBrand}
                fillOpacity={1}
                fill="url(#colorUv)"
              />
              <Tooltip
                content={(props) => (
                  <PerformanceMetricTooltip
                    active={props.active}
                    payload={props.payload}
                    coordinate={props.coordinate}
                    metricType={metricType}
                    timePeriod={timePeriod}
                  />
                )}
                position={tooltipPosition}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Chart>
      </RightContent>
    </>
  );
};
